import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Search, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlanItemsEditor } from "@/components/TemplatesTab";
import PreviewPlanModal from "@/components/PreviewPlanModal";
import PlanDaysEditor from "@/components/PlanDaysEditor";

export default function ClientPlansTab({ clients, exercises }) {
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const [days, setDays] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignForm, setAssignForm] = useState({ client_id: "", template_id: "", name: "", description: "", valid_from: "", valid_until: "" });
  const [editForm, setEditForm] = useState({ name: "", description: "", valid_from: "", valid_until: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPlan, setPreviewPlan] = useState(null);

  const openPreview = async (p) => {
    try {
      const { data } = await api.get(`/training-plans/${p.id}`);
      setPreviewPlan(data);
    } catch (_) {
      setPreviewPlan(p);
    }
    setPreviewOpen(true);
  };

  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const planStatus = (p) => {
    if (!p.valid_until) return "active";
    if (p.valid_until < today) return "expired";
    if (p.valid_until <= in7) return "expiring";
    return "active";
  };
  const filteredPlans = (() => {
    let base = plans;
    const s = q.trim().toLowerCase();
    if (s) base = base.filter(p => (p.client_name || "").toLowerCase().includes(s) || (p.name || "").toLowerCase().includes(s));
    if (statusFilter !== "all") base = base.filter(p => planStatus(p) === statusFilter);
    const sorted = [...base];
    switch (sortBy) {
      case "created_asc": sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")); break;
      case "created_desc": sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      case "valid_asc": sorted.sort((a, b) => (a.valid_until || "9999").localeCompare(b.valid_until || "9999")); break;
      case "valid_desc": sorted.sort((a, b) => (b.valid_until || "0000").localeCompare(a.valid_until || "0000")); break;
      default: break;
    }
    return sorted;
  })();

  const load = async () => {
    try {
      const [t, p] = await Promise.all([api.get("/templates"), api.get("/training-plans")]);
      setTemplates(t.data); setPlans(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const doAssign = async (e) => {
    e.preventDefault();
    if (!assignForm.client_id) return toast.error("Scegli un cliente");
    try {
      await api.post("/training-plans", {
        client_id: assignForm.client_id,
        template_id: assignForm.template_id || null,
        name: assignForm.name || null,
        description: assignForm.description,
        valid_from: assignForm.valid_from || null,
        valid_until: assignForm.valid_until || null,
      });
      toast.success("Scheda assegnata");
      setAssignOpen(false);
      setAssignForm({ client_id: "", template_id: "", name: "", description: "", valid_from: "", valid_until: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = async (p) => {
    try {
      const { data } = await api.get(`/training-plans/${p.id}`);
      setEditing(data);
      setEditForm({
        name: data.name, description: data.description || "",
        valid_from: data.valid_from || "", valid_until: data.valid_until || "",
      });
      setDays(data.days || []);
      setEditOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/training-plans/${editing.id}`, {
        name: editForm.name, description: editForm.description, days,
        valid_from: editForm.valid_from || null,
        valid_until: editForm.valid_until || null,
      });
      toast.success("Scheda aggiornata");
      setEditOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare scheda "${p.name}" di ${p.client_name}?`)) return;
    try {
      await api.delete(`/training-plans/${p.id}`);
      toast.success("Scheda eliminata");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="label-caps">Schede clienti</div>
          <h2 className="font-heading font-black uppercase tracking-tighter text-2xl mt-1">{filteredPlans.length}{filteredPlans.length !== plans.length ? ` / ${plans.length}` : ""} schede assegnate</h2>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={e => setQ(e.target.value)} data-testid="plans-search" placeholder="Cerca cliente o nome scheda"
              className="pl-9 pr-3 py-2 bg-transparent border border-white/15 focus:border-[#A855F7] outline-none text-sm text-white w-72" />
          </div>
          <select data-testid="plans-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="bg-transparent border border-white/15 p-2 text-white text-sm focus:border-[#A855F7] outline-none">
            <option value="created_desc" className="bg-[#120B19]">Creazione: più recenti</option>
            <option value="created_asc" className="bg-[#120B19]">Creazione: meno recenti</option>
            <option value="valid_asc" className="bg-[#120B19]">Scadenza: prima in scadenza</option>
            <option value="valid_desc" className="bg-[#120B19]">Scadenza: più lontane</option>
          </select>
          <button data-testid="assign-plan-btn" onClick={() => setAssignOpen(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Assegna scheda</button>
        </div>
      </div>
      <div className="inline-flex border border-white/10 mb-4">
        {[["all","Tutte"],["active","Attive"],["expiring","In scadenza"],["expired","Scadute"]].map(([k,l]) => (
          <button key={k} data-testid={`plans-filter-${k}`} onClick={() => setStatusFilter(k)}
            className={`px-4 py-2 font-heading uppercase tracking-widest text-xs transition-colors border-l first:border-l-0 border-white/10 ${statusFilter===k ? "bg-[#A855F7] text-white" : "text-white/60 hover:text-white"}`}>{l}</button>
        ))}
        </div>

      <div className="card-surface table-scroll">
        <table className="w-full min-w-[720px]">
          <thead className="border-b border-white/10">
            <tr>{["Cliente", "Scheda", "Esercizi", "Assegnata il", "Valida fino", ""].map(h => <th key={h} className="label-caps text-left p-4">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filteredPlans.map(p => {
              const expired = p.valid_until && p.valid_until < new Date().toISOString().slice(0, 10);
              return (
              <tr key={p.id} data-testid={`plan-row-${p.id}`} className={`border-b border-white/5 hover:bg-white/5 ${expired ? "opacity-60" : ""}`}>
                <td className="p-4 font-medium">{p.client_name}</td>
                <td className="p-4">{p.name}</td>
                <td className="p-4 text-white/60 text-sm">{(p.items || []).length}</td>
                <td className="p-4 text-white/50 text-xs">{p.assigned_at ? new Date(p.assigned_at).toLocaleDateString("it-IT") : "—"}</td>
                <td className="p-4 text-xs">
                  {p.valid_until ? (
                    <span className={expired ? "text-[#FF3366] font-bold" : "text-white/70"}>
                      {new Date(p.valid_until).toLocaleDateString("it-IT")}
                      {expired && " (scaduta)"}
                    </span>
                  ) : <span className="text-white/40">senza scadenza</span>}
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button data-testid={`preview-plan-${p.id}`} onClick={() => openPreview(p)} className="p-2 text-white/70 hover:text-[#A855F7]" title="Anteprima"><Eye size={14} /></button>
                  <button data-testid={`edit-plan-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-white/70 hover:text-white" title="Modifica"><Edit3 size={14} /></button>
                  <button data-testid={`del-plan-${p.id}`} onClick={() => remove(p)} className="p-2 text-[#FF3366]/70 hover:text-[#FF3366]" title="Elimina"><Trash2 size={14} /></button>
                </td>
              </tr>
            );})}
            {filteredPlans.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-white/40">Nessun risultato.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Preview dialog */}
      <PreviewPlanModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        plan={previewPlan}
        exercises={exercises}
      />

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Assegna scheda a cliente</DialogTitle></DialogHeader>
          <form onSubmit={doAssign} className="grid gap-4">
            <F label="Cliente">
              <select required data-testid="assign-client" value={assignForm.client_id} onChange={e => setAssignForm({ ...assignForm, client_id: e.target.value })} className="fld2">
                <option value="" className="bg-[#120B19]">Scegli…</option>
                {clients.map(c => <option key={c.id} value={c.id} className="bg-[#120B19]">{c.display_name || c.name}</option>)}
              </select>
            </F>
            <F label="Template (opzionale)">
              <select data-testid="assign-template" value={assignForm.template_id} onChange={e => setAssignForm({ ...assignForm, template_id: e.target.value })} className="fld2">
                <option value="" className="bg-[#120B19]">Nessuno (scheda vuota)</option>
                {templates.map(t => <option key={t.id} value={t.id} className="bg-[#120B19]">{t.name} ({(t.items || []).length} es.)</option>)}
              </select>
            </F>
            <F label="Nome scheda (se vuoto usa quello del template)">
              <input data-testid="assign-name" value={assignForm.name} onChange={e => setAssignForm({ ...assignForm, name: e.target.value })} className="fld2" />
            </F>
            <F label="Descrizione">
              <textarea rows={2} value={assignForm.description} onChange={e => setAssignForm({ ...assignForm, description: e.target.value })} className="fld2" />
            </F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Valida dal"><input type="date" data-testid="assign-valid-from" value={assignForm.valid_from} onChange={e => setAssignForm({ ...assignForm, valid_from: e.target.value })} className="fld2" /></F>
              <F label="Valida fino a"><input type="date" data-testid="assign-valid-until" value={assignForm.valid_until} onChange={e => setAssignForm({ ...assignForm, valid_until: e.target.value })} className="fld2" /></F>
            </div>
            <button type="submit" data-testid="assign-submit" className="btn-primary mt-2">Assegna</button>
          </form>
          <style>{`.fld2{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.15);padding:10px 12px;color:#fff;outline:none;font-family:'Manrope',sans-serif;font-size:0.95rem}.fld2:focus{border-color:#A855F7}`}</style>
        </DialogContent>
      </Dialog>

      {/* Edit plan dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#120B19] border-white/10 text-white rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">
            Modifica scheda {editing ? "· " + editing.client_name : ""}
          </DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="grid gap-4">
            <F label="Nome scheda"><input required value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="fld2" data-testid="edit-plan-name" /></F>
            <F label="Descrizione"><textarea rows={2} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} className="fld2" /></F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Valida dal"><input type="date" data-testid="edit-valid-from" value={editForm.valid_from} onChange={e => setEditForm({ ...editForm, valid_from: e.target.value })} className="fld2" /></F>
              <F label="Valida fino a"><input type="date" data-testid="edit-valid-until" value={editForm.valid_until} onChange={e => setEditForm({ ...editForm, valid_until: e.target.value })} className="fld2" /></F>
            </div>
            <PlanDaysEditor days={days} setDays={setDays} exercises={exercises} />
            <button type="submit" data-testid="edit-plan-submit" className="btn-primary mt-2">Salva</button>
          </form>
          <style>{`.fld2{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.15);padding:10px 12px;color:#fff;outline:none;font-family:'Manrope',sans-serif;font-size:0.95rem}.fld2:focus{border-color:#A855F7}`}</style>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) { return <label className="block"><span className="label-caps block mb-2">{label}</span>{children}</label>; }
